import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DollarSign, Percent, Store, Package, Loader2, TrendingUp, AlertCircle, Search, Check, ChevronsUpDown } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { admin } from '@/lib/api';
import { toast } from 'sonner';

const PriceManagementTab = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(true);
  const [priceStats, setPriceStats] = useState(null);
  const [sellers, setSellers] = useState([]);
  
  // Form state
  const [markupPercentage, setMarkupPercentage] = useState('40');
  const [selectedSeller, setSelectedSeller] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [sellerPopoverOpen, setSellerPopoverOpen] = useState(false);

  // Sort sellers alphabetically by store_name or name
  const sortedSellers = useMemo(() => {
    return [...sellers].sort((a, b) => {
      const nameA = (a.store_name || a.name || a.email || '').toLowerCase();
      const nameB = (b.store_name || b.name || b.email || '').toLowerCase();
      return nameA.localeCompare(nameB, 'tr');
    });
  }, [sellers]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setStatsLoading(true);
      const [statsRes, sellersRes] = await Promise.allSettled([
        admin.getPriceStats(),
        admin.allSellers()
      ]);
      if (statsRes.status === 'fulfilled') setPriceStats(statsRes.value.data);
      if (sellersRes.status === 'fulfilled') {
        const sellerData = sellersRes.value.data;
        setSellers(Array.isArray(sellerData) ? sellerData : sellerData.sellers || []);
      }
    } catch (error) {
      console.error('Failed to fetch price stats:', error);
      toast.error('Fiyat istatistikleri yüklenemedi');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleBulkPriceUpdate = async () => {
    const percentage = parseFloat(markupPercentage);
    if (isNaN(percentage) || percentage < 0 || percentage > 500) {
      toast.error('Geçerli bir yüzde girin (0-500)');
      return;
    }

    try {
      setLoading(true);
      const data = {
        markup_percentage: percentage,
        seller_id: selectedSeller === 'all' ? null : selectedSeller,
        category: selectedCategory || null
      };
      
      const response = await admin.bulkPriceUpdate(data);
      toast.success(`${response.data.updated_count} ürün fiyatı güncellendi (+%${percentage})`);
      fetchData(); // Refresh stats
    } catch (error) {
      console.error('Bulk price update failed:', error);
      toast.error(error.response?.data?.detail || 'Fiyat güncellemesi başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="price-management-tab">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Toplu Fiyat Yönetimi
          </h2>
          <p className="text-muted-foreground mt-1">
            Tüm satıcıların ürünlerine toplu kar marjı uygulayın
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {statsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Toplam Ürün
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {priceStats?.total_products || 0}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Aktif Satıcı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">
                {Object.keys(priceStats?.seller_stats || {}).length}
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Ortalama Kar Marjı
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">
                %{(() => {
                  const stats = priceStats?.seller_stats || {};
                  const markups = Object.values(stats).map(s => s.avg_markup || 0).filter(m => m > 0);
                  return markups.length ? (markups.reduce((a, b) => a + b, 0) / markups.length).toFixed(0) : '0';
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Bulk Update Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Toplu Fiyat Güncelleme
          </CardTitle>
          <CardDescription>
            Seçilen satıcı ve kategorilere kar marjı uygulayın. Maliyet fiyatı (base_price) korunur, sadece satış fiyatı güncellenir.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              <strong>Önemli:</strong> Bu işlem satıcıların göremeyeceği şekilde fiyatları günceller. 
              Satıcılar sadece satış fiyatını görür, maliyet fiyatı ve kar marjı gizli kalır.
            </AlertDescription>
          </Alert>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Markup Percentage */}
            <div className="space-y-2">
              <Label htmlFor="markup">Kar Marjı (%)</Label>
              <div className="relative">
                <Percent className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="markup"
                  type="number"
                  min="0"
                  max="500"
                  value={markupPercentage}
                  onChange={(e) => setMarkupPercentage(e.target.value)}
                  className="pl-10"
                  placeholder="40"
                  data-testid="markup-percentage-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">
                Örn: %40 kar marjı ile 100 TL maliyetli ürün → 140 TL satış fiyatı
              </p>
            </div>

            {/* Seller Filter */}
            <div className="space-y-2">
              <Label>Satici</Label>
              <Popover open={sellerPopoverOpen} onOpenChange={setSellerPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" role="combobox" className="w-full justify-between font-normal" data-testid="seller-select">
                    <div className="flex items-center gap-2 truncate">
                      <Store className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="truncate">
                        {selectedSeller === 'all'
                          ? 'Tum Saticilar'
                          : (() => {
                              const s = sellers.find(s => s.id === selectedSeller);
                              return s ? (s.store_name || s.name || s.email) : 'Secildi';
                            })()
                        }
                      </span>
                    </div>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[340px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Satici ara..." data-testid="seller-search-input" />
                    <CommandList>
                      <CommandEmpty>Satici bulunamadi</CommandEmpty>
                      <CommandGroup>
                        <CommandItem value="all" onSelect={() => { setSelectedSeller('all'); setSellerPopoverOpen(false); }}>
                          <Check className={`mr-2 h-4 w-4 ${selectedSeller === 'all' ? 'opacity-100' : 'opacity-0'}`} />
                          <Store className="mr-2 h-4 w-4 text-muted-foreground" />
                          Tum Saticilar
                        </CommandItem>
                        {sortedSellers.map((seller) => (
                          <CommandItem
                            key={seller.id}
                            value={`${seller.store_name || ''} ${seller.name || ''} ${seller.email || ''}`}
                            onSelect={() => { setSelectedSeller(seller.id); setSellerPopoverOpen(false); }}
                          >
                            <Check className={`mr-2 h-4 w-4 ${selectedSeller === seller.id ? 'opacity-100' : 'opacity-0'}`} />
                            <div className="truncate">
                              <span className="font-medium">{seller.store_name || seller.name || seller.email}</span>
                              {seller.store_name && seller.name && seller.store_name !== seller.name && (
                                <span className="text-xs text-muted-foreground ml-1">({seller.name})</span>
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label htmlFor="category">Kategori (Opsiyonel)</Label>
              <div className="relative">
                <Package className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  id="category"
                  type="text"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="pl-10"
                  placeholder="Örn: Etek, Ayakkabı..."
                  data-testid="category-input"
                />
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleBulkPriceUpdate}
              disabled={loading}
              size="lg"
              className="min-w-[200px]"
              data-testid="apply-markup-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Güncelleniyor...
                </>
              ) : (
                <>
                  <TrendingUp className="w-4 h-4 mr-2" />
                  Kar Marjı Uygula
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Seller Stats Table */}
      {priceStats?.seller_stats && Object.keys(priceStats.seller_stats).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Satıcı Bazlı İstatistikler</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium">Satıcı</th>
                    <th className="text-left py-3 px-4 font-medium">Ürün Sayısı</th>
                    <th className="text-left py-3 px-4 font-medium">Kar Marjı</th>
                    <th className="text-left py-3 px-4 font-medium">XML Kaynağı</th>
                    <th className="text-left py-3 px-4 font-medium">Son Güncelleme</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(priceStats.seller_stats).map(([sellerId, stats]) => {
                    const seller = sellers.find(s => s.id === sellerId);
                    return (
                      <tr key={sellerId} className="border-b hover:bg-muted/50">
                        <td className="py-3 px-4">
                          <span className="font-medium">
                            {seller?.name || seller?.storeName || seller?.email || sellerId.slice(0, 8) + '...'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="secondary">{stats.product_count}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          {stats.avg_markup > 0 ? (
                            <Badge variant="default" className="bg-green-600">
                              %{stats.avg_markup.toFixed(0)}
                            </Badge>
                          ) : (
                            <Badge variant="outline">Belirlenmedi</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {seller?.xml_source ? (
                            <span className="text-xs text-blue-600 truncate max-w-[150px] block" title={seller.xml_source}>
                              {seller.xml_source.length > 30 ? seller.xml_source.substring(0, 30) + '...' : seller.xml_source}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {seller?.xml_import_date ? (
                            <span className="text-xs">
                              {new Date(seller.xml_import_date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PriceManagementTab;
